import { createRouter, createWebHistory } from 'vue-router';
import { isAuthenticated } from '@/api/management/auth';
// Import views
import SignIn from '@/views/management/auth/SignIn.vue';
import Dashboard from '@/views/management/dashboard/Dashboard.vue';
import BlankPage from '@/views/management/BlankPage.vue';
// Helper function to create blank page route
const createBlankRoute = (path, title) => ({
    path,
    name: path.replace(/\//g, '-').replace(/^-/, ''),
    component: BlankPage,
    props: {
        pageTitle: title,
        breadcrumbs: [{ label: title, path, active: true }]
    },
    meta: {
        requiresAuth: true,
        title: `${title} | پلّه`
    }
});
const routes = [
    // Public Website Routes
    {
        path: '/f/:shortCode',
        name: 'PublicForm',
        component: () => import('@/views/public/website/PublicFormPage.vue'),
        meta: {
            requiresAuth: false,
            title: 'فرم'
        }
    },
    {
        path: '/website/:organizationSlug',
        name: 'WebsiteIntro',
        component: () => import('@/views/public/website/Intro.vue'),
        meta: {
            requiresAuth: false,
            title: 'صفحه اصلی'
        }
    },
    {
        path: '/website/:organizationSlug/menu',
        name: 'WebsiteMenu',
        component: () => import('@/views/public/website/Menu.vue'),
        meta: {
            requiresAuth: false,
            title: 'منو'
        }
    },
    // Management Routes
    {
        path: '/',
        name: 'SignIn',
        component: SignIn,
        meta: {
            requiresAuth: false,
            title: 'ورود به سیستم | پلّه'
        }
    },
    {
        path: '/signin',
        redirect: '/'
    },
    {
        path: '/signup',
        name: 'SignUp',
        component: () => import('@/views/management/auth/SignUp.vue'),
        meta: {
            requiresAuth: false,
            title: 'ثبت نام | پلّه'
        }
    },
    // Main Dashboard
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
            requiresAuth: true,
            title: 'داشبورد | پلّه'
        }
    },
    // Website Settings
    {
        path: '/website/settings',
        name: 'WebsiteSettings',
        component: () => import('@/views/management/website/Settings.vue'),
        meta: {
            requiresAuth: true,
            title: 'تنظیمات وب سایت آنلاین | پلّه'
        }
    },
    {
        path: '/website/menu-forms',
        name: 'WebsiteMenuForms',
        component: () => import('@/views/management/website/MenuForms.vue'),
        meta: {
            requiresAuth: true,
            title: 'فرم‌های منو | پلّه'
        }
    },
    {
        path: '/website/menu-forms/new',
        name: 'WebsiteMenuFormCreate',
        component: () => import('@/views/management/website/MenuFormEditor.vue'),
        meta: {
            requiresAuth: true,
            title: 'فرم جدید منو | پلّه'
        }
    },
    {
        path: '/website/menu-forms/:id/submissions',
        name: 'WebsiteMenuFormSubmissions',
        component: () => import('@/views/management/website/MenuFormSubmissions.vue'),
        meta: {
            requiresAuth: true,
            title: 'پاسخ‌های فرم منو | پلّه'
        }
    },
    {
        path: '/website/menu-forms/:id',
        name: 'WebsiteMenuFormEdit',
        component: () => import('@/views/management/website/MenuFormEditor.vue'),
        meta: {
            requiresAuth: true,
            title: 'ویرایش فرم منو | پلّه'
        }
    },
    // Branch Menu Routes - Dashboards
    createBlankRoute('/dashboard-agent', 'نمایندگان'),
    createBlankRoute('/dashboard-customer', 'مشتری'),
    // Branch Menu Routes - Property
    createBlankRoute('/property-grid', 'گرید املاک'),
    createBlankRoute('/property-list', 'لیست املاک'),
    createBlankRoute('/property-details', 'جزئیات املاک'),
    createBlankRoute('/property-add', 'اضافه کردن املاک'),
    // Branch Menu Routes - Agents
    createBlankRoute('/agents-list', 'لیست ویو'),
    createBlankRoute('/agents-grid', 'گرید ویو'),
    createBlankRoute('/agents-details', 'جزئیات نمایندگان'),
    createBlankRoute('/agents-add', 'اضافه کردن'),
    // Branch Menu Routes - Customers
    createBlankRoute('/customers-list', 'لیست ویو'),
    createBlankRoute('/customers-grid', 'گرید ویو'),
    createBlankRoute('/customers-details', 'جزئیات مشتری'),
    createBlankRoute('/customers-add', 'اضافه کردن مشتری'),
    // Branch Menu Routes - Other
    createBlankRoute('/orders', 'سفارشات'),
    createBlankRoute('/transactions', 'تراکنش ها'),
    createBlankRoute('/reviews', 'بررسی ها'),
    createBlankRoute('/messages', 'پیام ها'),
    createBlankRoute('/inbox', 'اینباکس'),
    // Branch Menu Routes - Blog
    createBlankRoute('/post', 'مقالات'),
    createBlankRoute('/post-details', 'جزئیات مقالات'),
    createBlankRoute('/post-create', 'اضافه کردن مقاله'),
    // Branch Menu Routes - Pages
    createBlankRoute('/pages-starter', 'خوش آمدید'),
    createBlankRoute('/pages-calendar', 'تقویم'),
    createBlankRoute('/pages-invoice', 'فاکتور'),
    createBlankRoute('/pages-faqs', 'سوالات متداول'),
    createBlankRoute('/pages-comingsoon', 'به زودی'),
    createBlankRoute('/pages-timeline', 'جدول زمانی'),
    createBlankRoute('/pages-pricing', 'قیمت گذاری'),
    createBlankRoute('/pages-maintenance', 'تعمیر'),
    createBlankRoute('/pages-404', 'ارور ۴۰۴'),
    createBlankRoute('/pages-404-alt', 'ارور ۴۰۴ «alt»'),
    // Branch Menu Routes - Authentication
    createBlankRoute('/auth-signin', 'ورود'),
    createBlankRoute('/auth-signup', 'ثبت نام'),
    createBlankRoute('/auth-password', 'تغییر رمزعبور'),
    createBlankRoute('/auth-lock-screen', 'قفل کردن صفحه'),
    // Branch Menu Routes - Widgets
    createBlankRoute('/widgets', 'ویجت ها'),
    // Branch Menu Routes - Layouts
    createBlankRoute('/layouts-dark-sidenav', 'سایدبار دارک'),
    createBlankRoute('/layouts-dark-topnav', 'تاپ بار دارک'),
    createBlankRoute('/layouts-simple-sidenav', 'سایدبار ساده'),
    createBlankRoute('/layouts-small-sidenav', 'سایدبار کوچک'),
    createBlankRoute('/layouts-small-hover', 'هاور کوچک'),
    createBlankRoute('/layouts-small-hover-active', 'هاور اکتیو کوچک'),
    createBlankRoute('/layouts-hidden-sidenav', 'سایدبار مخفی'),
    createBlankRoute('/layouts-dark', 'ورژن دارک'),
    // Branch Menu Routes - UI Components
    createBlankRoute('/ui-accordion', 'آکاردئون'),
    createBlankRoute('/ui-alerts', 'هشدارها'),
    createBlankRoute('/ui-avatar', 'آواتار'),
    createBlankRoute('/ui-badge', 'نشان‌ها'),
    createBlankRoute('/ui-breadcrumb', 'مسیر‌یاب'),
    createBlankRoute('/ui-buttons', 'دکمه‌ها'),
    createBlankRoute('/ui-card', 'کارت'),
    createBlankRoute('/ui-carousel', 'اسلایدر'),
    createBlankRoute('/ui-collapse', 'باز و بسته‌شونده'),
    createBlankRoute('/ui-dropdown', 'منوی کشویی'),
    createBlankRoute('/ui-list-group', 'لیست گروهی'),
    createBlankRoute('/ui-modal', 'مودال (پنجره پاپ‌آپ)'),
    createBlankRoute('/ui-tabs', 'تب‌ها'),
    createBlankRoute('/ui-offcanvas', 'منوی کناری'),
    createBlankRoute('/ui-pagination', 'صفحه‌بندی'),
    createBlankRoute('/ui-placeholders', 'جای‌نگهدار'),
    createBlankRoute('/ui-popovers', 'پاپ‌اور'),
    createBlankRoute('/ui-progress', 'نوار پیشرفت'),
    createBlankRoute('/ui-scrollspy', 'اسکرول‌اسپای'),
    createBlankRoute('/ui-spinners', 'لودینگ‌ها'),
    createBlankRoute('/ui-toasts', 'اعلان‌ها'),
    createBlankRoute('/ui-tooltips', 'راهنماها'),
    // Branch Menu Routes - Extended UI
    createBlankRoute('/extended-ratings', 'رتبه بندی ها'),
    createBlankRoute('/extended-sweetalert', 'هشدار شیرین'),
    createBlankRoute('/extended-swiper-silder', 'اسلایدر سوایپر'),
    createBlankRoute('/extended-scrollbar', 'نوار پیمایش'),
    createBlankRoute('/extended-toastify', 'برشته کردن'),
    // Branch Menu Routes - Charts
    createBlankRoute('/charts-apex-area', 'منطقه'),
    createBlankRoute('/charts-apex-bar', 'میله‌ای'),
    createBlankRoute('/charts-apex-bubble', 'حبابی'),
    createBlankRoute('/charts-apex-candlestick', 'شمعی'),
    createBlankRoute('/charts-apex-column', 'ستونی'),
    createBlankRoute('/charts-apex-heatmap', 'نقشه حرارتی'),
    createBlankRoute('/charts-apex-line', 'خطی'),
    createBlankRoute('/charts-apex-mixed', 'ترکیبی'),
    createBlankRoute('/charts-apex-timeline', 'جدول زمانی'),
    createBlankRoute('/charts-apex-boxplot', 'جعبه‌ای'),
    createBlankRoute('/charts-apex-treemap', 'نقشه درختی'),
    createBlankRoute('/charts-apex-pie', 'دایره‌ای'),
    createBlankRoute('/charts-apex-radar', 'رادار'),
    createBlankRoute('/charts-apex-radialbar', 'میله شعاعی'),
    createBlankRoute('/charts-apex-scatter', 'پراکندگی'),
    createBlankRoute('/charts-apex-polar-area', 'قطبی'),
    // Branch Menu Routes - Forms
    createBlankRoute('/forms-basic', 'عناصر پایه'),
    createBlankRoute('/forms-checkbox-radio', 'چک‌باکس و رادیو'),
    createBlankRoute('/forms-choices', 'انتخابگر گزینه'),
    createBlankRoute('/forms-clipboard', 'کلیپ‌بورد'),
    createBlankRoute('/forms-flatepicker', 'انتخابگر تاریخ'),
    createBlankRoute('/forms-validation', 'اعتبارسنجی'),
    createBlankRoute('/forms-wizard', 'فرم چندمرحله‌ای'),
    createBlankRoute('/forms-fileuploads', 'آپلود فایل'),
    createBlankRoute('/forms-editors', 'ویرایشگرها'),
    createBlankRoute('/forms-input-mask', 'ماسک ورودی'),
    createBlankRoute('/forms-range-slider', 'اسلایدر'),
    // Branch Menu Routes - Tables
    createBlankRoute('/tables-basic', 'جدول ساده'),
    createBlankRoute('/tables-gridjs', 'گرید جی اس'),
    // Branch Menu Routes - Icons
    createBlankRoute('/icons-remix', 'آیکن های رمیکس'),
    createBlankRoute('/icons-solar', 'آیکن های سولار'),
    // Branch Menu Routes - Maps
    createBlankRoute('/maps-google', 'نقشه های گوگل'),
    createBlankRoute('/maps-vector', 'نقشه های وکتور'),
    // Accounting Routes
    {
        path: '/accounting/dashboard',
        name: 'AccountingDashboard',
        component: () => import('@/views/management/accounting/Dashboard.vue'),
        meta: {
            requiresAuth: true,
            title: 'داشبورد حسابداری | پلّه'
        }
    },
    createBlankRoute('/accounting/transactions', 'همه تراکنش‌ها'),
    createBlankRoute('/accounting/receipts', 'دریافت‌ها'),
    createBlankRoute('/accounting/payments', 'پرداخت‌ها'),
    createBlankRoute('/accounting/reports/financial', 'گزارش مالی'),
    createBlankRoute('/accounting/reports/profit-loss', 'گزارش سود و زیان'),
    createBlankRoute('/accounting/reports/balance-sheet', 'ترازنامه'),
    createBlankRoute('/accounting/invoices', 'فاکتورها'),
    createBlankRoute('/accounting/checks', 'چک‌ها'),
    // CRM Routes
    {
        path: '/crm/dashboard',
        name: 'CRMDashboard',
        component: () => import('@/views/management/crm/Dashboard.vue'),
        meta: {
            requiresAuth: true,
            title: 'داشبورد CRM | پلّه'
        }
    },
    createBlankRoute('/crm/customers', 'لیست مشتریان'),
    createBlankRoute('/crm/customers/add', 'اضافه کردن مشتری'),
    createBlankRoute('/crm/customers/groups', 'گروه‌های مشتری'),
    createBlankRoute('/crm/leads', 'همه سرنخ‌ها'),
    createBlankRoute('/crm/leads/convert', 'تبدیل به مشتری'),
    createBlankRoute('/crm/activities', 'فعالیت‌ها'),
    createBlankRoute('/crm/tickets', 'تیکت‌ها'),
    createBlankRoute('/crm/campaigns', 'کمپین‌ها'),
    // Products Routes
    {
        path: '/products/categories',
        name: 'ProductsCategories',
        component: () => import('@/views/management/products/Categories.vue'),
        meta: {
            requiresAuth: true,
            title: 'دسته‌بندی‌ها | پلّه'
        }
    },
    {
        path: '/products/list',
        name: 'ProductsList',
        component: () => import('@/views/management/products/Products.vue'),
        meta: {
            requiresAuth: true,
            title: 'محصولات | پلّه'
        }
    },
    {
        path: '/products/addons',
        name: 'ProductAddons',
        component: () => import('@/views/management/products/ProductAddons.vue'),
        meta: {
            requiresAuth: true,
            title: 'افزودنی‌های محصول | پلّه'
        }
    },
    {
        path: '/products/tags',
        name: 'ProductTags',
        component: () => import('@/views/management/products/ProductTags.vue'),
        meta: {
            requiresAuth: true,
            title: 'برچسب‌های محصول | پلّه'
        }
    },
    // Restaurant orders
    {
        path: '/orders/floor',
        name: 'OrderFloor',
        component: () => import('@/views/management/orders/OrderFloor.vue'),
        meta: {
            requiresAuth: true,
            title: 'سالن و ثبت سفارش | پلّه'
        }
    },
    {
        path: '/orders/list',
        name: 'OrdersList',
        component: () => import('@/views/management/orders/OrdersList.vue'),
        meta: {
            requiresAuth: true,
            title: 'لیست سفارش‌ها | پلّه'
        }
    },
    {
        path: '/orders/invoices',
        name: 'InvoicesList',
        component: () => import('@/views/management/orders/InvoicesList.vue'),
        meta: {
            requiresAuth: true,
            title: 'فاکتورها | پلّه'
        }
    },
    {
        path: '/orders/tables',
        name: 'TablesManagement',
        component: () => import('@/views/management/orders/TablesManagement.vue'),
        meta: {
            requiresAuth: true,
            title: 'مدیریت میزها | پلّه'
        }
    },
    {
        path: '/orders/printer-settings',
        name: 'PrinterSettings',
        component: () => import('@/views/management/printing/PrinterSettings.vue'),
        meta: {
            requiresAuth: true,
            title: 'تنظیمات چاپگر | پلّه'
        }
    }
];
const router = createRouter({
    history: createWebHistory(),
    routes
});
// Navigation guard to check authentication
router.beforeEach((to, from, next) => {
    // Set page title
    if (to.meta.title) {
        document.title = to.meta.title;
    }
    // Check if route requires authentication
    if (to.meta.requiresAuth) {
        if (isAuthenticated()) {
            next();
        }
        else {
            // Redirect to sign in if not authenticated
            next({ name: 'SignIn', query: { redirect: to.fullPath } });
        }
    }
    else {
        // If user is already authenticated and trying to access auth pages, redirect to dashboard
        if ((to.name === 'SignIn' || to.name === 'SignUp') && isAuthenticated()) {
            next({ name: 'Dashboard' });
        }
        else {
            next();
        }
    }
});
export default router;
